import './index.scss'

// Loading 管理器
export const Loading = {
  // 存储所有 loading 实例
  instances: [],
  // 实例计数器，用于生成唯一 ID
  instanceId: 0,

  // 显示 loading
  show(options = {}) {
    const {
      text = '',
      target = null, // 目标元素，如果为 null 则全屏显示
      background = false, // 是否显示背景，默认不显示
      className = '', // 自定义类名
      overlayClass = '', // 遮罩层自定义类名
      containerClass = '', // 容器自定义类名
      zIndex = 2000, // 层级
      pointerEvents = '', // 点击穿透设置
      onOpened = null, // 打开后回调
      onClose = null, // 关闭回调
      ...restOptions // 其他参数
    } = options

    // 合并全局配置和传入的参数
    const mergedParams = {
      ...(window.HDM?.LoadingGlobalOptions || {}),
      background,
      className,
      overlayClass,
      containerClass,
      zIndex,
      pointerEvents,
      onOpened,
      onClose,
      ...restOptions,
    }

    // 创建实例
    const instance = this.createInstance({
      id: `hdm-loading-${++this.instanceId}`,
      text,
      target,
      params: mergedParams,
    })

    // 保存实例
    this.instances.push(instance)

    // 显示 loading
    this.showInstance(instance)

    // 创建实例包装对象
    const instanceWrapper = {
      id: instance.id,
      hide: () => {
        this.hideInstance(instance.id)
      },
    }

    // 在下一个事件循环中调用 onOpened 回调
    if (mergedParams.onOpened && typeof mergedParams.onOpened === 'function') {
      setTimeout(() => {
        mergedParams.onOpened(instanceWrapper)
      }, 0)
    }

    return instanceWrapper
  },

  // 创建 loading 实例
  createInstance({ id, text, target, params }) {
    // 默认显示背景
    params.background = params.background ?? false
    // 创建遮罩层
    const overlay = document.createElement('div')

    // 创建 loading 容器
    const loadingContainer = document.createElement('div')

    // 创建 loading 内容
    const loadingContent = document.createElement('div')
    // 根据是否显示背景添加对应的 class
    const backgroundClass = params.background === false ? 'loading-content-no-bg' : 'loading-content'
    loadingContent.className = params.className
      ? `${backgroundClass} ${params.className}`
      : backgroundClass

    // 创建加载动画
    const spinner = document.createElement('div')
    spinner.className = 'loading-spinner'
    loadingContent.appendChild(spinner)

    // 添加文本（如果有）
    if (text) {
      const textNode = document.createElement('div')
      textNode.className = 'loading-text'
      textNode.textContent = text
      loadingContent.appendChild(textNode)
    }

    loadingContainer.appendChild(loadingContent)
    overlay.appendChild(loadingContainer)

    return {
      id,
      text,
      target,
      params,
      overlay,
      loadingContainer,
      loadingContent,
    }
  },

  // 显示实例
  showInstance(instance) {
    const { overlay, loadingContainer, target, params } = instance

    if (target) {
      // 绑定到指定元素
      overlay.className = params.overlayClass
        ? `loading-overlay loading-overlay-local ${params.overlayClass}`
        : 'loading-overlay loading-overlay-local'

      loadingContainer.className = params.containerClass
        ? `loading-container ${params.containerClass}`
        : 'loading-container'

      // 设置目标元素为相对定位（如果不是）
      const targetPosition = window.getComputedStyle(target).position
      if (targetPosition === 'static') {
        target.style.position = 'relative'
      }

      // 将遮罩层添加到目标元素中
      target.appendChild(overlay)
    } else {
      // 全屏显示
      overlay.className = params.overlayClass
        ? `loading-overlay loading-overlay-global ${params.overlayClass}`
        : 'loading-overlay loading-overlay-global'

      loadingContainer.className = params.containerClass
        ? `loading-container ${params.containerClass}`
        : 'loading-container'

      // 将遮罩层添加到 body 中
      document.body.appendChild(overlay)
    }

    // 设置遮罩层样式
    if (params.pointerEvents) {
      overlay.style.pointerEvents = params.pointerEvents
    }

    overlay.style.zIndex = params?.zIndex || 2000
    overlay.style.display = 'flex'
  },

  // 隐藏指定的 loading
  hide(id) {
    if (!id) {
      // 如果没有传入 ID，关闭最后一个实例
      if (this.instances.length > 0) {
        const lastInstance = this.instances[this.instances.length - 1]
        this.hideInstance(lastInstance.id)
      }
      return
    }

    this.hideInstance(id)
  },

  // 隐藏实例
  hideInstance(id) {
    const index = this.instances.findIndex(instance => instance.id === id)

    if (index === -1) {
      return
    }

    const instance = this.instances[index]
    const { overlay, params } = instance

    // 执行关闭回调
    if (params.onClose && typeof params.onClose === 'function') {
      params.onClose()
    }

    // 移除 DOM 元素
    if (overlay && overlay.parentNode) {
      overlay.parentNode.removeChild(overlay)
    }

    // 从实例列表中移除
    this.instances.splice(index, 1)
  },

  // 隐藏所有 loading
  hideAll() {
    // 从后往前遍历，避免索引问题
    for (let i = this.instances.length - 1; i >= 0; i--) {
      this.hideInstance(this.instances[i].id)
    }
  },

  // 隐藏指定元素上的所有 loading
  hideByTarget(target) {
    const targetInstances = this.instances.filter(
      instance => instance.target === target,
    )

    targetInstances.forEach(instance => {
      this.hideInstance(instance.id)
    })
  },

  // 设置全局默认配置
  setDefaultOptions(options) {
    if (!window.HDM) {
      window.HDM = {}
    }
    window.HDM.LoadingGlobalOptions = options
  },

  // 重置全局默认配置
  resetDefaultOptions() {
    if (window.HDM) {
      window.HDM.LoadingGlobalOptions = null
    }
  },
}
